import { createActionCreators } from 'immer-reducer';
import { goBack, push } from 'connected-react-router';

import { ProjectsReducer } from '@/store/reducers/projects';

import { AddProjectBody, EditProjectBody, EditTeammateBody } from '@/api/main-protected';

import { revalidate } from '@/store/responses';

import { AsyncAction } from './common';

export const projectsActions = createActionCreators(ProjectsReducer);

export type ProjectsActions =
  | ReturnType<typeof projectsActions.setProject>
  | ReturnType<typeof projectsActions.setProjects>
  | ReturnType<typeof projectsActions.setUsersProjects>
  | ReturnType<typeof projectsActions.cleanProject>
  | ReturnType<typeof projectsActions.cleanProjects>
  | ReturnType<typeof projectsActions.setTags>
  | ReturnType<typeof projectsActions.setHours>
  | ReturnType<typeof projectsActions.setIsSaveLoading>
  | ReturnType<typeof projectsActions.setSlackChannels>
  | ReturnType<typeof projectsActions.setProjectsLight>
  | ReturnType<typeof projectsActions.cleanProjectsLight>;

// Two fields with two writers each: getProjects and getCustomerProjects both
// fill `projects`, getProjectsLight and getProjectsArchive both fill
// `projectsLight`.
const PROJECTS_SLOT = 'projectsReducer.projects';
const PROJECTS_LIGHT_SLOT = 'projectsReducer.projectsLight';

export const getProjects = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      '/projects',
      () => mainProtectedApi.getProjects(),
      (projects) => dispatch(projectsActions.setProjects(projects)),
      { slot: PROJECTS_SLOT },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getProjectsLight = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    // The same request as getProjects, applied to the other field.
    await revalidate(
      '/projects',
      () => mainProtectedApi.getProjectsLight(),
      (projects) => dispatch(projectsActions.setProjectsLight(projects)),
      { slot: PROJECTS_LIGHT_SLOT },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getProjectsArchive = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      '/projects/archive',
      () => mainProtectedApi.getProjectsArchive(),
      (projects) => dispatch(projectsActions.setProjectsLight(projects)),
      { slot: PROJECTS_LIGHT_SLOT },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getUserProjects = (id:number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      `/projects/user/${id}`,
      () => mainProtectedApi.getUserProjects(id),
      (projects) => dispatch(projectsActions.setUsersProjects(projects)),
      { slot: 'projectsReducer.usersProjects' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getCustomerProjects = (id:number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      `/projects/customer/${id}`,
      () => mainProtectedApi.getCustomerProjects(id),
      (projects) => dispatch(projectsActions.setProjects(projects)),
      { slot: PROJECTS_SLOT },
    );
  } catch (e) {
    console.log(e);
  }
};

// `fresh` for an edit form, which must not be filled from the stored copy --
// see skipStored in store/responses.ts.
export const getProject = (id: number, fresh = false): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      `/projects/${id}`,
      () => mainProtectedApi.getProject(id),
      (project) => dispatch(projectsActions.setProject(project)),
      { slot: 'projectsReducer.project', skipStored: fresh },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getSlackChannels = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    const response = await mainProtectedApi.getSlackChannels();
    dispatch(projectsActions.setSlackChannels(response));
  } catch (e) {
    console.log(e);
  }
};

export const cleanProject = (): AsyncAction => async (
  dispatch,
) => {
  try {
    dispatch(projectsActions.cleanProject());
  } catch (e) {
    console.log(e);
  }
};

export const cleanProjects = (): AsyncAction => async (
  dispatch,
) => {
  try {
    dispatch(projectsActions.cleanProjects());
  } catch (e) {
    console.log(e);
  }
};

export const cleanProjectsLight = (): AsyncAction => async (
  dispatch,
) => {
  try {
    dispatch(projectsActions.cleanProjectsLight());
  } catch (e) {
    console.log(e);
  }
};

export const addProject = (body: AddProjectBody): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    dispatch(projectsActions.setIsSaveLoading(true));

    await mainProtectedApi.addProject(body);

    dispatch(push('/projects'));
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(projectsActions.setIsSaveLoading(false));
  }
};

export const editProject = (body: EditProjectBody, id: number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    dispatch(projectsActions.setIsSaveLoading(true));

    await mainProtectedApi.editProject(body, id);
    dispatch(getProject(id));
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(projectsActions.setIsSaveLoading(false));
    dispatch(goBack());
  }
};

export const disconnectSlack = (id: number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    dispatch(projectsActions.setIsSaveLoading(true));

    await mainProtectedApi.disconnectSlack(id);
    dispatch(getProject(id));
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(projectsActions.setIsSaveLoading(false));
  }
};

export const addTeammate = (projectId: number, userId: number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    dispatch(projectsActions.setIsSaveLoading(true));

    await mainProtectedApi.addTeammate(projectId, userId);

    dispatch(getProject(projectId));
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(projectsActions.setIsSaveLoading(false));
  }
};

export const deleteTeammate = (projectId: number, userId: number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    dispatch(projectsActions.setIsSaveLoading(true));

    await mainProtectedApi.deleteTeammate(projectId, userId);

    dispatch(getProject(projectId));
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(projectsActions.setIsSaveLoading(false));
  }
};

export const addCustomerOnProject = (projectId: number, customerId: number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    dispatch(projectsActions.setIsSaveLoading(true));

    await mainProtectedApi.addCustomerOnProject(projectId, customerId);

    dispatch(getProject(projectId));
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(projectsActions.setIsSaveLoading(false));
  }
};

export const deleteCustomerFromProject = (
  projectId: number,
  customerId: number,
): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    dispatch(projectsActions.setIsSaveLoading(true));

    await mainProtectedApi.deleteCustomerFromProject(projectId, customerId);

    dispatch(getProject(projectId));
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(projectsActions.setIsSaveLoading(false));
  }
};

export const editTeammate = (body: EditTeammateBody, bondId: string): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    dispatch(projectsActions.setIsSaveLoading(true));

    await mainProtectedApi.editTeammate(body, bondId);
    dispatch(getProject(body.projectId));
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(projectsActions.setIsSaveLoading(false));
  }
};

export const getProjectsTags = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      '/projects/tags',
      () => mainProtectedApi.getProjectsTags(),
      (tags) => dispatch(projectsActions.setTags(tags)),
      { slot: 'projectsReducer.tags' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getProjectsHours = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      '/projects/hours',
      () => mainProtectedApi.getProjectsHours(),
      (hours) => dispatch(projectsActions.setHours(hours)),
      { slot: 'projectsReducer.hours' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const toggleProjectStatus = (id: number, status: boolean): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    if (status) {
      await mainProtectedApi.toArchiveProject(id);
    } else {
      await mainProtectedApi.restoreProject(id);
    }

    dispatch(goBack());
  } catch (e) {
    console.log(e);
  }
};
